import React, { useState, useEffect, useRef } from "react";
import "./dashboard.css"; // Assuming you have a corresponding CSS file for styling
import Logo from "./public/logo.svg";
import chev from "./public/chev1-down.svg";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import { User, LogOut, MapPin, Menu, ChevronDown, X, Building, Maximize2, Minimize2 } from "lucide-react";
import ArthaContinueButton from "../fields/ArthaContinueButton";
import ArthaCancelButton from "../fields/ArthaCancelButton";
import ArthaTRACK from "../Assets/LOGO.png";
import { organisationData, setLocationFilter, clearLocationFilter, setSuperAdminOrgFilter, clearSuperAdminOrgFilter } from "../actions/loanAction";
import {
  logoutAPI,
  fetchDashboardData,
  fetchDashboardLocations,
  fetchAllOrganizationsList,
  fetchOrganizationLocations,
} from "../actioncreators/actioncreators";
import { useDispatch, useSelector } from "react-redux";
const Header = ({ onMenuToggle, isMobileMenuOpen, isTabletExpanded }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dashboardData, setDashboardData] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fullscreen toggle function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      // Exit fullscreen
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  };

  // Listen for fullscreen change events (e.g., when user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Get user role from Redux
  const loginResponse = useSelector((state) => state.loginResponse);
  const userRole = loginResponse?.role_name || loginResponse?.roleName || '';
  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  // Organization filter state (for Super Admin)
  const [organizations, setOrganizations] = useState([]);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const orgDropdownRef = useRef(null);

  // Get selected organization from Redux (for Super Admin)
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);

  // Location filter state
  const [locations, setLocations] = useState([]);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const locationDropdownRef = useRef(null);

  // Get selected location from Redux
  const selectedLocationId = useSelector((state) => state.selectedLocationFilter);
  const selectedLocationData = useSelector((state) => state.locationFilterData);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  // Fetch organizations for Super Admin dropdown
  useEffect(() => {
    if (isSuperAdmin) {
      const loadOrganizations = async () => {
        try {
          setOrgsLoading(true);
          const response = await fetchAllOrganizationsList();
          if (response.data?.data && Array.isArray(response.data.data)) {
            setOrganizations(response.data.data);
          }
        } catch (error) {
          console.error('Error loading organizations:', error);
        } finally {
          setOrgsLoading(false);
        }
      };
      loadOrganizations();
    }
  }, [isSuperAdmin]);

  // Fetch locations for the filter dropdown
  // For Super Admin: fetch based on selected organization
  // For regular users: fetch their own locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);
        if (isSuperAdmin && superAdminSelectedOrgId) {
          // Super Admin with selected org: fetch that org's locations
          const response = await fetchOrganizationLocations(superAdminSelectedOrgId);
          if (response.data?.data && Array.isArray(response.data.data)) {
            setLocations(response.data.data);
          } else {
            setLocations([]);
          }
        } else if (!isSuperAdmin) {
          // Regular user: fetch their own locations
          const response = await fetchDashboardLocations();
          if (response.viewListData && Array.isArray(response.viewListData)) {
            setLocations(response.viewListData);
          }
        } else {
          // Super Admin without selected org: no locations to show
          setLocations([]);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, [isSuperAdmin, superAdminSelectedOrgId]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setIsOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle organization selection (Super Admin)
  const handleSelectOrganization = (org) => {
    if (org) {
      dispatch(setSuperAdminOrgFilter(org.org_id, org));
    } else {
      dispatch(clearSuperAdminOrgFilter());
    }
    setIsOrgDropdownOpen(false);
  };

  // Handle clear organization selection
  const handleClearOrgSelection = (e) => {
    e.stopPropagation();
    dispatch(clearSuperAdminOrgFilter());
    setIsOrgDropdownOpen(false);
  };

  // Get selected organization name for display
  const selectedOrgName = superAdminSelectedOrgId
    ? (superAdminSelectedOrgData?.org_name || organizations.find(org => org.org_id === superAdminSelectedOrgId)?.org_name || 'Selected Org')
    : 'Select Organization';

  useEffect(() => {
    const fetchDashboardData1 = async () => {
      try {
        const response = await fetchDashboardData();
        dispatch(organisationData(response.data.dashboardData));
        setDashboardData(response.data.dashboardData);
        setSubscriptionDetails(response.data.subscriptionDetails);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData1();
  }, []);

  // Handle location selection
  const handleSelectLocation = (location) => {
    if (location) {
      dispatch(setLocationFilter(location.location_id, location));
    } else {
      dispatch(clearLocationFilter());
    }
    setIsLocationDropdownOpen(false);
  };

  // Handle clear location selection
  const handleClearLocationSelection = (e) => {
    e.stopPropagation();
    dispatch(clearLocationFilter());
    setIsLocationDropdownOpen(false);
  };

  // Get selected location name for display
  const selectedLocationName = selectedLocationId
    ? (selectedLocationData?.location_name || locations.find(loc => loc.location_id === selectedLocationId)?.location_name || 'Selected Location')
    : 'All Locations';

  const handleLogout = async () => {
    try {
      const response = await logoutAPI();
      if (response.data.businessStatusCode === 2) {
        localStorage.clear();
        navigate("/");
      } else {
        localStorage.clear();
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    closeDialog();
  };

  // Calculate days left and package name
  let daysLeft = null;
  let packageName = "";
  let showUpgradeButton = false;
  
  if (subscriptionDetails && subscriptionDetails.subscription_end_date) {
    const end = new Date(subscriptionDetails.subscription_end_date);
    const now = new Date();
    // Only count full days, not hours
    daysLeft = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    packageName = subscriptionDetails.package_name || "";
    
    // Show button always for Free Trial or if only 7 days left for other plans
    if (packageName === 'Free Trial' || daysLeft <= 7) {
      showUpgradeButton = true;
    }
  }

  return (
    <div className="row p-0 m-0 bg-color">
      <div className="col-12 d-flex align-items-center text-black-50 border-bottom py-1 shadow-sm px-2 px-md-4 header-container">
        {/* Hamburger Menu Button - visible only on mobile/tablet */}
        <button
          className="hamburger-btn d-lg-none me-2 p-2 bg-transparent border-0"
          onClick={onMenuToggle}
          aria-label="Toggle menu"
          style={{ color: "#fff" }}
        >
          {(isMobileMenuOpen || isTabletExpanded) ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Logo */}
        <div className="header-logo">
          <img
            src={ArthaTRACK}
            alt="ArthaTRACK"
            className="header-logo-img"
            style={{ height: '35px', width: 'auto', maxWidth: '145px' }}
          />
        </div>

        {/* User Info - hidden on very small screens */}
        <div className="ms-2 ms-md-5 d-none d-sm-block user-info">
          {dashboardData ? (
            <span className="font-bold ms-2 d-block" style={{ color: "#ececf0", fontSize: '12px' }}>
              Hello, {dashboardData?.user?.full_name}
              <br />
              <small className="d-none d-md-inline">{dashboardData?.organization?.org_name}</small>
            </span>
          ) : (
            <p className="m-0" style={{ color: "#ececf0", fontSize: '12px' }}>Loading...</p>
          )}
        </div>

        {/* Right Side Actions */}
        <div className="d-flex align-items-center ms-auto header-actions">
          <style>
            {`
              .upgrade-blink {
                animation: blink-animation 2s ease-in-out infinite, gradient-move 5s linear infinite;
                background: linear-gradient(100deg, #4f8cff 0%, #ffb366 50%, #ff7a00 100%);
                background-size: 400% 400%;
              }
              @keyframes blink-animation {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }
              @keyframes gradient-move {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
              .upgrade-cta-btn {
                display: flex;
                align-items: center;
                /* background handled by .upgrade-blink */
                background: linear-gradient(100deg, #4f8cff 0%, #ffb366 50%, #ff7a00 100%);
                background-size: 200% 200%;
                color: #fff;
                border: none;
                border-radius: 16px;
                padding: 2px 16px;
                font-size: 13px;
                font-weight: 600;
                min-width: 0;
                width: auto;
                box-shadow: 0 1px 4px rgba(44, 62, 80, 0.08);
                transition: background 0.2s, box-shadow 0.2s, filter 0.18s;
                line-height: 1.2;
                gap: 4px;
                outline: none;
                cursor: pointer;
            }
              .upgrade-cta-btn:hover {
                filter: brightness(1.08);
                box-shadow: 0 2px 8px rgba(44, 62, 80, 0.12);
                /* keep the same gradient on hover for consistency */
                background: linear-gradient(100deg, #4f8cff 0%, #ffb366 50%, #ff7a00 100%);
              }
              .upgrade-cta-icon {
                margin-right: 2px;
                font-size: 15px;
                display: flex;
                align-items: center;
                color: #fff;
              }
              .upgrade-cta-text {
                margin-left: 0;
                font-size: 13px;
                font-weight: 1000;
                letter-spacing: 0.01em;
                color: #fff;
              }
              .location-filter-dropdown {
                position: absolute;
                top: 100%;
                right: 0;
                margin-top: 4px;
                width: 280px;
                background: white;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                z-index: 1000;
                max-height: 320px;
                overflow-y: auto;
              }
              .location-filter-item {
                padding: 12px 16px;
                cursor: pointer;
                border-bottom: 1px solid #f3f4f6;
                transition: background-color 0.15s;
              }
              .location-filter-item:hover {
                background-color: #f9fafb;
              }
              .location-filter-item:last-child {
                border-bottom: none;
              }
              .location-filter-item.selected {
                background-color: #eff6ff;
              }
              .org-filter-dropdown {
                position: absolute;
                top: 100%;
                right: 0;
                margin-top: 4px;
                width: 300px;
                background: white;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                z-index: 1000;
                max-height: 350px;
                overflow-y: auto;
              }
              .org-filter-item {
                padding: 12px 16px;
                cursor: pointer;
                border-bottom: 1px solid #f3f4f6;
                transition: background-color 0.15s;
              }
              .org-filter-item:hover {
                background-color: #f9fafb;
              }
              .org-filter-item:last-child {
                border-bottom: none;
              }
              .org-filter-item.selected {
                background-color: #fef3c7;
              }
              .org-status-badge {
                display: inline-block;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 10px;
                font-weight: 1000;
                margin-left: 8px;
              }
              .org-status-active {
                background-color: #d1fae5;
                color: #065f46;
              }
              .org-status-inactive {
                background-color: #fee2e2;
                color: #991b1b;
              }
            `}
          </style>

          {/* Organization Filter Dropdown (Super Admin Only) */}
          {isSuperAdmin && (
            <div className="relative me-2" ref={orgDropdownRef}>
              <button
                onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                disabled={orgsLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors text-xs font-medium text-amber-800"
                style={{ minWidth: '180px' }}
              >
                <Building size={14} className="text-amber-600" />
                <span className="flex-1 text-left truncate" style={{ maxWidth: '120px' }}>
                  {orgsLoading ? 'Loading...' : selectedOrgName}
                </span>
                {superAdminSelectedOrgId && !orgsLoading && (
                  <X
                    size={12}
                    className="text-amber-400 hover:text-amber-600"
                    onClick={handleClearOrgSelection}
                  />
                )}
                <ChevronDown
                  size={14}
                  className={`text-amber-400 transition-transform ${isOrgDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOrgDropdownOpen && !orgsLoading && (
                <div className="org-filter-dropdown">
                  <div
                    className={`org-filter-item ${!superAdminSelectedOrgId ? 'selected' : ''}`}
                    onClick={() => handleSelectOrganization(null)}
                  >
                    <div className="flex items-center gap-2">
                      <Building size={14} className={!superAdminSelectedOrgId ? 'text-amber-600' : 'text-gray-400'} />
                      <span className="font-medium text-sm">All Organizations</span>
                    </div>
                    <p className="text-xs text-gray-1000 mt-1 ml-5">Super Admin view (no org filter)</p>
                  </div>

                  {organizations.length === 0 ? (
                    <div className="org-filter-item text-center text-gray-1000 text-sm">
                      No organizations found
                    </div>
                  ) : (
                    organizations.map((org) => (
                      <div
                        key={org.org_id}
                        className={`org-filter-item ${superAdminSelectedOrgId === org.org_id ? 'selected' : ''}`}
                        onClick={() => handleSelectOrganization(org)}
                      >
                        <div className="flex items-center gap-2">
                          <Building
                            size={14}
                            className={superAdminSelectedOrgId === org.org_id ? 'text-amber-600' : 'text-gray-400'}
                          />
                          <span className="font-medium text-sm truncate">{org.org_name}</span>
                          <span className={`org-status-badge ${org.status === 'ACTIVE' ? 'org-status-active' : 'org-status-inactive'}`}>
                            {org.status || 'N/A'}
                          </span>
                        </div>
                        {org.package_name && (
                          <p className="text-xs text-gray-1000 mt-1 ml-5 truncate">
                            Plan: {org.package_name}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Global Location Filter Dropdown - Show for regular users OR when Super Admin has selected an org */}
          {(!isSuperAdmin || superAdminSelectedOrgId) && (
          <div className="relative" ref={locationDropdownRef}>
            <button
              onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
              disabled={locationsLoading}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium text-gray-700"
              style={{ minWidth: '160px' }}
            >
              <MapPin size={14} className="text-blue-600" />
              <span className="flex-1 text-left truncate" style={{ maxWidth: '100px' }}>
                {locationsLoading ? 'Loading...' : selectedLocationName}
              </span>
              {selectedLocationId && !locationsLoading && (
                <X
                  size={12}
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleClearLocationSelection}
                />
              )}
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform ${isLocationDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isLocationDropdownOpen && !locationsLoading && (
              <div className="location-filter-dropdown">
                <div
                  className={`location-filter-item ${!selectedLocationId ? 'selected' : ''}`}
                  onClick={() => handleSelectLocation(null)}
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className={!selectedLocationId ? 'text-blue-600' : 'text-gray-400'} />
                    <span className="font-medium text-sm">All Locations</span>
                  </div>
                  <p className="text-xs text-gray-1000 mt-1 ml-5">Show data from all locations</p>
                </div>

                {locations.length === 0 ? (
                  <div className="location-filter-item text-center text-gray-1000 text-sm">
                    No locations found
                  </div>
                ) : (
                  locations.map((location) => (
                    <div
                      key={location.location_id}
                      className={`location-filter-item ${selectedLocationId === location.location_id ? 'selected' : ''}`}
                      onClick={() => handleSelectLocation(location)}
                    >
                      <div className="flex items-center gap-2">
                        <MapPin
                          size={14}
                          className={selectedLocationId === location.location_id ? 'text-blue-600' : 'text-gray-400'}
                        />
                        <span className="font-medium text-sm truncate">{location.location_name}</span>
                      </div>
                      {location.location_address && (
                        <p className="text-xs text-gray-1000 mt-1 ml-5 truncate">
                          {location.location_address}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          )}

          {/* {showUpgradeButton && (
            <button
              className="upgrade-cta-btn upgrade-blink ms-2"
              onClick={() => navigate("/home/subscription")}
              type="button"
            >
              <span className="upgrade-cta-icon">
                <i className="bi bi-arrow-up-right-circle-fill"></i>
              </span>
              <span className="upgrade-cta-text">
                {daysLeft !== null && packageName
                  ? `${daysLeft} days left in ${packageName}`
                  : "Loading..."}
              </span>
            </button>
          )} */}

          {/* Fullscreen Toggle Button */}
          <button
            className="p-2 bg-gray-200 rounded-full d-flex align-items-center justify-content-center ms-2"
            style={{ width: '32px', height: '32px' }}
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="text-gray-600" size={16} />
            ) : (
              <Maximize2 className="text-gray-600" size={16} />
            )}
          </button>

          <button className="p-2 bg-gray-200 rounded-full d-flex align-items-center justify-content-center ms-2" style={{ width: '32px', height: '32px' }}>
            <User className="text-gray-600" size={16} />
          </button>
          <button className="p-2 bg-gray-200 rounded-full d-flex align-items-center justify-content-center ms-2" style={{ width: '32px', height: '32px' }} onClick={openDialog}>
            <LogOut
              className="text-gray-600"
              style={{ color: "#fd5f07" }}
              size={16}
            />
          </button>
          <Dialog
            open={isDialogOpen}
            onClose={closeDialog}
            className="text-center"
          >
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogContent>
              <span>Are you sure you want to log out?</span>
            </DialogContent>
            <div className="text-center py-2">
              <ArthaContinueButton onClick={handleLogout}>
                Log Out
              </ArthaContinueButton>
              <ArthaCancelButton onClick={closeDialog}>
                Cancel
              </ArthaCancelButton>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Header;
